/** @jsxImportSource @emotion/react */
import { debounce, isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { spacing } from '../../theme/spacing';
import { Card } from '../Card';
import { Checkbox } from '../Checkbox';
import { Input } from '../Input';
import { List } from '../List';
import { ListItem } from '../ListItem';
import { Typography } from '../Typography';
import { UtilityButton } from '../UtilityButton';

export type Option = {
  value: string;
  label: string;
};

export interface Props {
  label?: string;
  listTitle?: string;
  value: Option | Option[] | null;
  options: Option[];
  onSelect: (data: Option | Option[] | null) => void;
  multiple?: boolean;
  searchable?: boolean;
}

export const UtilitySelect = ({
  label,
  listTitle,
  options,
  onSelect,
  value,
  multiple = false,
  searchable = false,
}: Props) => {
  const [search, setSearch] = useState('');
  const [allOptions, setAllOptions] = useState(options);

  const debounceSearch = useMemo(
    () =>
      debounce(() => {
        if (search)
          setAllOptions(
            options.filter((val) => val.label.toLowerCase().includes(search))
          );
        else setAllOptions(options);
      }, 500),
    [search, options]
  );

  useEffect(() => {
    debounceSearch();
  }, [search, debounceSearch]);

  const handleOnSelect = useCallback(
    (data: Option, close: () => void) => {
      if (multiple) {
        onSelect(
          value === null
            ? [data]
            : Array.isArray(value)
              ? value.map((item) => item.value).includes(data.value)
                ? value.filter((e) => e.value != data.value)
                : [...value, data]
              : null
        );
      } else {
        onSelect(value && isEqual(value, data) ? null : data);
        close();
        setSearch('');
      }
    },
    [multiple, onSelect, value]
  );

  return (
    <UtilityButton
      text={Array.isArray(value) ? label : value ? value.label : label}
      variant="tertiary"
      dropdown={({ close }) => (
        <Card>
          <div
            css={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: spacing.xxs,
            }}
          >
            <Typography variant="subtitle">{listTitle}</Typography>
            <UtilityButton
              variant="text"
              text="Reset"
              onClick={() => {
                onSelect(null);
                setSearch('');
                close();
              }}
            />
          </div>
          {searchable && (
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value.toLowerCase());
                debounceSearch.cancel();
              }}
              label="Search"
              css={{
                marginBottom: spacing.xxxs,
              }}
            />
          )}
          <div
            css={{
              maxHeight: '320px',
              overflowY: 'scroll',
            }}
          >
            <List>
              {allOptions.length !== 0 ? (
                allOptions.map((s) => (
                  <ListItem
                    key={s.value}
                    title={s.label}
                    onClick={() => handleOnSelect(s, close)}
                    action={
                      multiple && Array.isArray(value) ? (
                        <Checkbox
                          checked={
                            value.map((item) => item.value).includes(s.value) ||
                            false
                          }
                        />
                      ) : (
                        <Checkbox
                          checked={
                            !Array.isArray(value)
                              ? value?.value === s.value
                              : false
                          }
                        />
                      )
                    }
                  />
                ))
              ) : (
                <ListItem title="No result found" />
              )}
            </List>
          </div>
        </Card>
      )}
    />
  );
};
